import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { ChevronLeft, ChevronRight, FileText, Loader2, Pencil, RefreshCw, Search, Upload, X, Check } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  DEFAULT_REPORTING_YEAR,
  DEFAULT_REPORT_TYPE,
  ESG_REPORT_TYPES,
  BUSINESS_TYPES,
  REPORTING_PERIOD_TYPES,
  REPORTING_YEAR_OPTIONS,
  formatReportingYearLabel,
  labelForBusinessType,
  labelForReportType,
  labelForReportingPeriod,
  normalizeBusinessType,
  normalizeReportType,
  toSubmitYear,
} from '../lib/esgConstants';
import {
  buildEsgDocumentBlobPath,
  downloadFileFromSourceUrl,
  fileNameFromSourceUrl,
  parseEsgDocumentBlobPath,
  uploadEsgDocument,
} from '../lib/esgBlobStorage';
import {
  listBenchmarkDocuments,
  searchBenchmarkCompanies,
  submitBenchmarkDocument,
  updateBenchmarkDocument,
} from '../lib/portalApi';

const MIN_COMPANY_SEARCH_LENGTH = 2;
const TABLE_COLUMN_COUNT = 15;
const ACCEPTED_FILE_TYPES = '.pdf,.xlsx,.xls,.doc,.docx';

const INITIAL_FORM = {
  year: String(DEFAULT_REPORTING_YEAR),
  reportingPeriodType: 'FINANCIAL',
  reportType: DEFAULT_REPORT_TYPE,
  sourceUrl: '',
};

const INITIAL_FILTERS = { companyName: '', reportType: 'all', businessType: 'all', status: 'all' };

const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : '—');
const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
const asArray = (value) => (Array.isArray(value) ? value : []);

function RequiredLabel({ htmlFor, children }) {
  return (
    <Label htmlFor={htmlFor}>
      {children}
      <span className="text-destructive"> *</span>
    </Label>
  );
}

function ReportFileUpload({ file, onFileChange, disabled }) {
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    if (!file && fileInputRef.current) fileInputRef.current.value = '';
  }, [file]);

  const selectFile = (selectedFile) => onFileChange(selectedFile || null);

  return (
    <div className="space-y-2">
      <Label htmlFor="report-file">Report Document</Label>
      <p className="text-xs text-muted-foreground">
        Optional. If no file is uploaded, the report is downloaded automatically from the Source URL.
        Upload a file only when you want to use a local copy instead.
      </p>
      <input
        id="report-file"
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_FILE_TYPES}
        className="hidden"
        disabled={disabled}
        onChange={(event) => selectFile(event.target.files?.[0] || null)}
      />

      {!file ? (
        <div
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-disabled={disabled}
          onClick={() => !disabled && fileInputRef.current?.click()}
          onKeyDown={(event) => {
            if (!disabled && (event.key === 'Enter' || event.key === ' ')) {
              event.preventDefault();
              fileInputRef.current?.click();
            }
          }}
          onDragEnter={(event) => { event.preventDefault(); setDragActive(true); }}
          onDragLeave={(event) => { event.preventDefault(); setDragActive(false); }}
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => {
            event.preventDefault();
            setDragActive(false);
            if (!disabled && event.dataTransfer.files?.[0]) {
              selectFile(event.dataTransfer.files[0]);
            }
          }}
          className={cn(
            'flex flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-10 text-center transition-colors',
            disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:border-primary/50 hover:bg-muted/50',
            dragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25',
          )}
        >
          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Upload className="h-6 w-6 text-primary" />
          </div>
          <p className="text-sm font-medium">
            {dragActive ? 'Drop your file here' : 'Click to upload or drag and drop'}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            PDF, Excel, or Word documents
          </p>
        </div>
      ) : (
        <div className="flex items-center gap-3 rounded-lg border bg-muted/30 p-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10">
            <FileText className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{file.name}</p>
            <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="shrink-0 text-muted-foreground hover:text-destructive"
            onClick={() => selectFile(null)}
            disabled={disabled}
            aria-label="Remove file"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

function CompanySearchDropdown({ hasMinimumQuery, loading, trimmedQuery, results, onSelect, onCreateNew }) {
  if (!hasMinimumQuery) {
    return (
      <div className="absolute z-20 mt-1 w-full rounded-md border bg-popover px-3 py-2 text-sm text-muted-foreground shadow-md">
        Type at least {MIN_COMPANY_SEARCH_LENGTH} characters to search companies.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="absolute z-20 mt-1 w-full rounded-md border bg-popover px-3 py-3 shadow-md">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Searching companies...
        </div>
      </div>
    );
  }

  if (results.length > 0) {
    return (
      <div className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-md border bg-popover shadow-md">
        {results.map((company) => (
          <button
            key={company.greenStitchId ?? company.companyName}
            type="button"
            className="flex w-full flex-col items-start px-3 py-2 text-left text-sm hover:bg-accent"
            onClick={() => onSelect(company)}
          >
            <span className="font-medium">{company.companyName}</span>
            {company.greenStitchId && (
              <span className="text-muted-foreground">{company.greenStitchId}</span>
            )}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-md border bg-popover shadow-md">
      <button
        type="button"
        className="flex w-full flex-col items-start gap-1 px-3 py-3 text-left text-sm hover:bg-accent"
        onClick={onCreateNew}
      >
        <span className="font-bold">{trimmedQuery}</span>
        <span className="text-sm leading-snug">
          <span className="font-semibold text-foreground">No existing company</span>
          <span className="text-muted-foreground"> — </span>
          <span className="font-semibold text-primary">create new company</span>
        </span>
      </button>
    </div>
  );
}

function CompanySearchField({ selectedCompany, onSelect, onClear }) {
  const [query, setQuery] = useState(selectedCompany?.companyName || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const trimmedQuery = query.trim();
  const hasMinimumQuery = trimmedQuery.length >= MIN_COMPANY_SEARCH_LENGTH;
  const showDropdown = open && !selectedCompany;

  useEffect(() => {
    setQuery(selectedCompany?.companyName || '');
  }, [selectedCompany]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (selectedCompany || !hasMinimumQuery) {
      setResults([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setOpen(true);

    const timer = setTimeout(async () => {
      try {
        const companies = await searchBenchmarkCompanies(trimmedQuery);
        setResults(asArray(companies));
      } catch (error) {
        toast.error(error.message || 'Failed to search companies');
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [trimmedQuery, selectedCompany, hasMinimumQuery]);

  const handleSelect = (company) => {
    onSelect(company);
    setQuery(company.companyName);
    setOpen(false);
  };

  return (
    <div ref={containerRef} className="space-y-2">
      <RequiredLabel htmlFor="company-search">Company</RequiredLabel>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          id="company-search"
          value={query}
          placeholder="Search company name..."
          className="pl-9"
          onChange={(event) => {
            setQuery(event.target.value);
            if (!selectedCompany || event.target.value !== selectedCompany.companyName) {
              if (selectedCompany) onClear();
              setOpen(true);
            }
          }}
          onFocus={() => !selectedCompany && setOpen(true)}
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
        )}
        {showDropdown && (
          <CompanySearchDropdown
            hasMinimumQuery={hasMinimumQuery}
            loading={loading}
            trimmedQuery={trimmedQuery}
            results={results}
            onSelect={handleSelect}
            onCreateNew={() => handleSelect({ companyName: trimmedQuery, greenStitchId: null })}
          />
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        Enter at least {MIN_COMPANY_SEARCH_LENGTH} characters to see company suggestions.
      </p>

      {selectedCompany && (
        <div className="rounded-md border bg-muted/40 p-3 text-sm">
          <p className="font-medium">{selectedCompany.companyName}</p>
          {selectedCompany.greenStitchId ? (
            <p className="text-muted-foreground">GreenStitch ID: {selectedCompany.greenStitchId}</p>
          ) : (
            <p className="text-muted-foreground">
              <span className="font-semibold text-foreground">New company</span>
              {' — '}
              <span className="font-medium">GreenStitch ID will be created on submit</span>
            </p>
          )}
          {selectedCompany.reportingPeriodType && (
            <p className="text-muted-foreground">
              Default period: {labelForReportingPeriod(selectedCompany.reportingPeriodType)}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function DocumentStatusBadge({ processedAt }) {
  return processedAt
    ? <Badge variant="secondary">Done</Badge>
    : <Badge variant="outline">Pending</Badge>;
}

export default function EsgReportExtractionPage() {
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [year, setYear] = useState(INITIAL_FORM.year);
  const [reportingPeriodType, setReportingPeriodType] = useState(INITIAL_FORM.reportingPeriodType);
  const [reportType, setReportType] = useState(INITIAL_FORM.reportType);
  const [sourceUrl, setSourceUrl] = useState(INITIAL_FORM.sourceUrl);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitPhase, setSubmitPhase] = useState(null);
  const [uploadedBlobPath, setUploadedBlobPath] = useState('');

  const [documents, setDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(true);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [editingDocumentId, setEditingDocumentId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [savingDocumentId, setSavingDocumentId] = useState(null);

  const loadDocuments = useCallback(async () => {
    setLoadingDocuments(true);
    try {
      setDocuments(asArray(await listBenchmarkDocuments()));
    } catch (error) {
      toast.error(error.message || 'Failed to load documents');
      setDocuments([]);
    } finally {
      setLoadingDocuments(false);
    }
  }, []);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const handleCompanySelect = (company) => {
    setSelectedCompany(company);
    if (company.reportingPeriodType) {
      setReportingPeriodType(company.reportingPeriodType);
    }
  };

  const availableReportTypes = useMemo(
    () => [...new Set(documents.map((doc) => doc.reportType).filter(Boolean))].sort(),
    [documents],
  );

  const availableBusinessTypes = useMemo(
    () => BUSINESS_TYPES.map((option) => option.value),
    [],
  );

  useEffect(() => {
    if (filters.reportType !== 'all' && !availableReportTypes.includes(filters.reportType)) {
      setFilters((prev) => ({ ...prev, reportType: 'all' }));
    }
  }, [availableReportTypes, filters.reportType]);

  useEffect(() => {
    if (filters.businessType !== 'all' && !availableBusinessTypes.includes(filters.businessType)) {
      setFilters((prev) => ({ ...prev, businessType: 'all' }));
    }
  }, [availableBusinessTypes, filters.businessType]);

  const filteredDocuments = useMemo(() => {
    const companySearch = filters.companyName.toLowerCase();

    return documents.filter((doc) => {
      if (companySearch && !doc.benchmarkCompanyName?.toLowerCase().includes(companySearch)) {
        return false;
      }
      if (filters.reportType !== 'all' && doc.reportType !== filters.reportType) {
        return false;
      }
      if (filters.businessType !== 'all'
        && normalizeBusinessType(doc.businessType) !== filters.businessType) {
        return false;
      }
      const isDone = Boolean(doc.processedAt);
      if (filters.status === 'done' && !isDone) return false;
      if (filters.status === 'pending' && isDone) return false;
      return true;
    });
  }, [documents, filters]);

  // Pagination happens in the browser: all documents are already loaded, filtered above, then sliced per page
  const totalPages = Math.ceil(filteredDocuments.length / pageSize);
  const pagedDocuments = useMemo(
    () => filteredDocuments.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [filteredDocuments, currentPage, pageSize]
  );

  // Back to page 1 whenever the filters or page size change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, pageSize]);

  const isSubmitReady = Boolean(
    selectedCompany?.companyName?.trim()
    && reportingPeriodType
    && year
    && reportType
    && sourceUrl.trim(),
  );

  const resetForm = () => {
    setSelectedCompany(null);
    setYear(INITIAL_FORM.year);
    setReportingPeriodType(INITIAL_FORM.reportingPeriodType);
    setReportType(INITIAL_FORM.reportType);
    setSourceUrl(INITIAL_FORM.sourceUrl);
    setFile(null);
    setSubmitPhase(null);
    setUploadedBlobPath('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!selectedCompany) {
      toast.error('Please select a company');
      return;
    }

    const parsedYear = Number(year);
    if (!parsedYear || parsedYear < 1900 || parsedYear > 2100) {
      toast.error('Please select a valid reporting year');
      return;
    }

    if (!sourceUrl.trim()) {
      toast.error('Source URL is required');
      return;
    }

    const trimmedSourceUrl = sourceUrl.trim();
    const effectiveFileName = file?.name || fileNameFromSourceUrl(trimmedSourceUrl);
    const nextBlobPath = buildEsgDocumentBlobPath({
      companyName: selectedCompany.companyName,
      year: parsedYear,
      fileName: effectiveFileName,
    });

    if (documents.some((doc) => parseEsgDocumentBlobPath(doc.blobPath) === nextBlobPath)) {
      toast.error(`A file named "${effectiveFileName}" already exists for this company and year`);
      return;
    }

    setSubmitting(true);
    setUploadedBlobPath('');

    try {
      let fileToUpload = file;

      if (!fileToUpload) {
        setSubmitPhase('downloading');
        fileToUpload = await downloadFileFromSourceUrl(trimmedSourceUrl);
      }

      setSubmitPhase('uploading');
      const uploadResult = await uploadEsgDocument({
        companyName: selectedCompany.companyName,
        year: parsedYear,
        file: fileToUpload,
      });
      setUploadedBlobPath(uploadResult.blobPath);

      setSubmitPhase('submitting');

      await submitBenchmarkDocument({
        greenStitchId: selectedCompany.greenStitchId ?? null,
        companyName: selectedCompany.companyName,
        blobUrl: uploadResult.blobUrl,
        sourceUrl: trimmedSourceUrl,
        year: toSubmitYear(parsedYear, reportingPeriodType),
        reportingPeriodType,
        reportType,
      });

      toast.success('Document submitted for extraction');
      resetForm();
      await loadDocuments();
    } catch (error) {
      toast.error(error.message || 'Failed to submit document');
    } finally {
      setSubmitting(false);
      setSubmitPhase(null);
    }
  };

  const updateFilter = (key) => (value) => setFilters((prev) => ({ ...prev, [key]: value }));

  const startEdit = (doc) => {
    setEditingDocumentId(doc.documentId);
    setEditDraft({
      benchmarkCompanyName: doc.benchmarkCompanyName ?? '',
      businessType: normalizeBusinessType(doc.businessType),
      reportType: normalizeReportType(doc.reportType),
      city: doc.city ?? '',
      state: doc.state ?? '',
      country: doc.country ?? '',
    });
  };

  const cancelEdit = () => {
    setEditingDocumentId(null);
    setEditDraft(null);
  };

  const updateEditDraft = (key) => (value) => {
    setEditDraft((prev) => ({ ...prev, [key]: value }));
  };

  const saveEdit = async (documentId) => {
    if (!editDraft?.benchmarkCompanyName?.trim()) {
      toast.error('Benchmark company name is required');
      return;
    }

    setSavingDocumentId(documentId);
    try {
      const updated = await updateBenchmarkDocument(documentId, editDraft);
      setDocuments((prev) => prev.map((doc) => (
        doc.documentId === documentId ? { ...doc, ...updated } : doc
      )));
      toast.success('Document updated');
      cancelEdit();
    } catch (error) {
      toast.error(error.message || 'Failed to update document');
    } finally {
      setSavingDocumentId(null);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-8 animate-fade-in">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight">ESG Report Extraction</h1>
          <p className="text-muted-foreground mt-1">
            Upload ESG benchmark reports and track extraction status.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Submit Report</CardTitle>
            <CardDescription>
              Search for a company and provide a source URL. Upload a file only if you want to submit a local copy instead of auto-downloading.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form className="space-y-6" onSubmit={handleSubmit}>
              <CompanySearchField
                selectedCompany={selectedCompany}
                onSelect={handleCompanySelect}
                onClear={() => setSelectedCompany(null)}
              />

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <RequiredLabel>Reporting Period Type</RequiredLabel>
                  <Select value={reportingPeriodType} onValueChange={setReportingPeriodType}>
                    <SelectTrigger><SelectValue placeholder="Select period type" /></SelectTrigger>
                    <SelectContent>
                      {REPORTING_PERIOD_TYPES.map((option) => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <RequiredLabel htmlFor="reporting-year">Reporting Year</RequiredLabel>
                  <Select value={year} onValueChange={setYear}>
                    <SelectTrigger id="reporting-year">
                      <SelectValue placeholder="Select reporting year" />
                    </SelectTrigger>
                    <SelectContent>
                      {REPORTING_YEAR_OPTIONS.map((optionYear) => (
                        <SelectItem key={optionYear} value={String(optionYear)}>
                          {formatReportingYearLabel(optionYear, reportingPeriodType)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <RequiredLabel>Report Type</RequiredLabel>
                  <Select value={reportType} onValueChange={setReportType}>
                    <SelectTrigger><SelectValue placeholder="Select report type" /></SelectTrigger>
                    <SelectContent>
                      {ESG_REPORT_TYPES.map((option) => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2 md:col-span-2">
                  <RequiredLabel htmlFor="source-url">Source URL</RequiredLabel>
                  <Input
                    id="source-url"
                    type="url"
                    placeholder="https://example.com/report.pdf"
                    value={sourceUrl}
                    onChange={(event) => setSourceUrl(event.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Required. Used as the report source and for auto-download when no file is uploaded.
                  </p>
                </div>
              </div>

              <ReportFileUpload
                file={file}
                onFileChange={setFile}
                disabled={submitting}
              />

              {submitPhase === 'downloading' && (
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Downloading report from source URL...
                </p>
              )}

              {submitPhase === 'uploading' && (
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Uploading file to blob storage...
                </p>
              )}

              {submitPhase === 'submitting' && uploadedBlobPath && (
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  File uploaded to <span className="font-mono">{uploadedBlobPath}</span>. Submitting for extraction...
                </p>
              )}

              <div className="flex justify-end">
                <Button type="submit" disabled={submitting || !isSubmitReady}>
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {submitPhase === 'downloading'
                        ? 'Downloading...'
                        : submitPhase === 'uploading'
                          ? 'Uploading to blob...'
                          : 'Submitting...'}
                    </>
                  ) : (
                    <>
                      <Upload className="mr-2 h-4 w-4" />
                      Submit for Extraction
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle>Uploaded Reports</CardTitle>
              <CardDescription>All previously submitted benchmark documents.</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={loadDocuments} disabled={loadingDocuments}>
              <RefreshCw className={cn('mr-2 h-4 w-4', loadingDocuments && 'animate-spin')} />
              Refresh
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
              <Input
                placeholder="Filter by company..."
                value={filters.companyName}
                onChange={(event) => updateFilter('companyName')(event.target.value)}
              />
              <Select value={filters.reportType} onValueChange={updateFilter('reportType')}>
                <SelectTrigger><SelectValue placeholder="Report type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All report types</SelectItem>
                  {availableReportTypes.map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filters.businessType} onValueChange={updateFilter('businessType')}>
                <SelectTrigger><SelectValue placeholder="Business type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All business types</SelectItem>
                  {availableBusinessTypes.map((type) => (
                    <SelectItem key={type} value={type}>{labelForBusinessType(type)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filters.status} onValueChange={updateFilter('status')}>
                <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="done">Done</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Document ID</TableHead>
                    <TableHead>GreenStitch ID</TableHead>
                    <TableHead>Benchmark Company</TableHead>
                    <TableHead>Business Type</TableHead>
                    <TableHead>Report Type</TableHead>
                    <TableHead>Year</TableHead>
                    <TableHead>Reporting Period Type</TableHead>
                    <TableHead>Source Path</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>City</TableHead>
                    <TableHead>State</TableHead>
                    <TableHead>Country</TableHead>
                    <TableHead>Created At</TableHead>
                    <TableHead>Processed At</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loadingDocuments ? (
                    <TableRow>
                      <TableCell colSpan={TABLE_COLUMN_COUNT} className="py-8 text-center text-muted-foreground">
                        <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />
                        Loading documents...
                      </TableCell>
                    </TableRow>
                  ) : filteredDocuments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={TABLE_COLUMN_COUNT} className="py-8 text-center text-muted-foreground">
                        No documents found
                      </TableCell>
                    </TableRow>
                  ) : (
                    pagedDocuments.map((doc) => {
                      const isEditing = editingDocumentId === doc.documentId;
                      const isSaving = savingDocumentId === doc.documentId;

                      return (
                      <TableRow key={doc.documentId}>
                        <TableCell>{doc.documentId ?? '—'}</TableCell>
                        <TableCell className="font-mono text-xs">{doc.greenStitchId ?? '—'}</TableCell>
                        <TableCell>
                          {isEditing ? (
                            <Input
                              value={editDraft.benchmarkCompanyName}
                              onChange={(event) => updateEditDraft('benchmarkCompanyName')(event.target.value)}
                              disabled={isSaving}
                            />
                          ) : (doc.benchmarkCompanyName ?? '—')}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <Select
                              value={editDraft.businessType}
                              onValueChange={updateEditDraft('businessType')}
                              disabled={isSaving}
                            >
                              <SelectTrigger><SelectValue placeholder="Business type" /></SelectTrigger>
                              <SelectContent>
                                {BUSINESS_TYPES.map((option) => (
                                  <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : labelForBusinessType(doc.businessType)}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <Select
                              value={editDraft.reportType}
                              onValueChange={updateEditDraft('reportType')}
                              disabled={isSaving}
                            >
                              <SelectTrigger><SelectValue placeholder="Report type" /></SelectTrigger>
                              <SelectContent>
                                {ESG_REPORT_TYPES.map((option) => (
                                  <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (labelForReportType(doc.reportType))}
                        </TableCell>
                        <TableCell>{doc.year ?? '—'}</TableCell>
                        <TableCell>{labelForReportingPeriod(doc.reportingPeriodType)}</TableCell>
                        <TableCell className="max-w-[220px] truncate" title={doc.sourcePath}>
                          {doc.sourcePath ? (
                            <a href={doc.sourcePath} target="_blank" rel="noreferrer" className="text-primary hover:underline">
                              {doc.sourcePath}
                            </a>
                          ) : '—'}
                        </TableCell>
                        <TableCell><DocumentStatusBadge processedAt={doc.processedAt} /></TableCell>
                        <TableCell>
                          {isEditing ? (
                            <Input
                              value={editDraft.city}
                              onChange={(event) => updateEditDraft('city')(event.target.value)}
                              disabled={isSaving}
                            />
                          ) : (doc.city ?? '—')}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <Input
                              value={editDraft.state}
                              onChange={(event) => updateEditDraft('state')(event.target.value)}
                              disabled={isSaving}
                            />
                          ) : (doc.state ?? '—')}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <Input
                              value={editDraft.country}
                              onChange={(event) => updateEditDraft('country')(event.target.value)}
                              disabled={isSaving}
                            />
                          ) : (doc.country ?? '—')}
                        </TableCell>
                        <TableCell>{formatDateTime(doc.createdAt)}</TableCell>
                        <TableCell>{formatDateTime(doc.processedAt)}</TableCell>
                        <TableCell className="text-right">
                          {isEditing ? (
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => saveEdit(doc.documentId)}
                                disabled={isSaving}
                                aria-label="Save changes"
                              >
                                {isSaving
                                  ? <Loader2 className="h-4 w-4 animate-spin" />
                                  : <Check className="h-4 w-4" />}
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={cancelEdit}
                                disabled={isSaving}
                                aria-label="Cancel edit"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => startEdit(doc)}
                              disabled={editingDocumentId !== null}
                              aria-label="Edit document"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>

            {filteredDocuments.length > 0 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4">
                <div className="flex items-center gap-4">
                  <span className="text-sm text-muted-foreground">
                    Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, filteredDocuments.length)} of {filteredDocuments.length} reports
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Rows per page:</span>
                    <Select value={String(pageSize)} onValueChange={(value) => setPageSize(Number(value))}>
                      <SelectTrigger className="w-[70px] h-8" data-testid="esg-page-size-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[10, 20, 50, 100].map((size) => (
                          <SelectItem key={size} value={String(size)}>{size}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1} data-testid="esg-prev-page-btn">
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">Page {currentPage} of {totalPages}</span>
                  <Button variant="outline" size="sm" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} data-testid="esg-next-page-btn">
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
